import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Bell, Menu, Settings, User, LogOut, UserCircle, CreditCard, X, Bookmark, PlusCircle, Users, Loader2, Play, Pause, SkipBack, SkipForward } from "lucide-react";
import { Capacitor } from '@capacitor/core';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { StableAvatar } from "@/components/ui/stable-avatar";
import { InstantAvatar } from "@/components/ui/instant-avatar";
import { SearchBar } from "@/components/SearchBar";
import { maskEmail, getFirstName } from "@/lib/utils";
import { IOSCacheRefreshButton } from "@/components/IOSCacheRefreshButton";
import { AuthModal } from "@/components/AuthModal";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { NotificationDropdown } from "@/components/NotificationDropdown";
import { useAppConfig } from "@/hooks/use-app-config";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useCurrencyDetection } from "@/hooks/use-currency-detection";
import { SmartPaywall } from "@/components/subscription/SmartPaywall";
import { useProfileHierarchy } from "@/hooks/use-profile-hierarchy";
import { useSharedElement } from "@/contexts/SharedElementContext";
import { preloadAvatar, isAvatarPreloaded } from "@/lib/avatarPreloader";
import { getProfileSnapshot, ProfileSnapshot, onAvatarChange } from "@/lib/profileSnapshot";
import { useRoxboxPlayerContext } from "@/contexts/RoxboxPlayerContext";
import { MobileBottomNav } from "@/components/navigation/MobileBottomNav";
import { MobileProfileSheet } from "@/components/navigation/MobileProfileSheet";
import { DynamicIslandPlayer } from "@/components/navigation/DynamicIslandPlayer";
import { useAppFlow } from "@/contexts/AppFlowContext";
import { isDespiaRuntime } from "@/hooks/use-despia";

import { signOutCompletely } from "@/utils/authCleanup";
import { hapticLight } from "@/lib/haptics";

// Desktop Header Mini Player with skip controls
const DesktopHeaderMiniPlayer = () => {
  const navigate = useNavigate();
  const { 
    currentTrack, 
    currentAudiobookPage, 
    isPlaying, 
    togglePlay,
    playingType,
    setNowPlayingOpen,
    nextTrack,
    previousTrack
  } = useRoxboxPlayerContext();
  
  const displayImage = playingType === 'audiobook' 
    ? currentAudiobookPage?.imageUrl 
    : currentTrack?.cover_image_url;
    
  const displayTitle = playingType === 'audiobook'
    ? `Page ${currentAudiobookPage?.pageNumber}`
    : currentTrack?.name;
  const displaySubtitle = playingType === 'audiobook'
    ? currentAudiobookPage?.bookTitle
    : currentTrack?.artist;

  const handleTap = () => {
    navigate('/v2');
    setNowPlayingOpen(true);
  };

  return (
    <div className="flex items-center gap-2.5 px-2 py-1">
      {/* Album art */}
      <button
        onClick={handleTap}
        className="relative w-8 h-8 shrink-0 rounded-md overflow-hidden hover:opacity-80 transition-opacity"
      >
        <img
          src={displayImage}
          alt={displayTitle || 'Now playing'}
          className="w-full h-full object-cover"
        />
      </button>
      
      {/* Track info */}
      <button 
        onClick={handleTap}
        className="min-w-0 text-left hover:opacity-80 transition-opacity"
        style={{ maxWidth: '120px' }}
      >
        <p className="text-white text-xs font-semibold truncate leading-tight">{displayTitle}</p>
        {displaySubtitle && (
          <p className="text-white/50 text-[10px] truncate leading-tight">{displaySubtitle}</p>
        )}
      </button>
      
      {/* Playback controls */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => previousTrack()}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
        >
          <SkipBack className="w-3.5 h-3.5 text-white fill-white" />
        </button>
        
        <button
          onClick={() => togglePlay()}
          className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        >
          {isPlaying ? (
            <Pause className="w-3.5 h-3.5 text-black fill-black" />
          ) : (
            <Play className="w-3.5 h-3.5 text-black fill-black ml-0.5" />
          )}
        </button>
        
        <button
          onClick={() => nextTrack()}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
        >
          <SkipForward className="w-3.5 h-3.5 text-white fill-white" />
        </button>
      </div>
    </div>
  );
};

export function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const lastScrollY = useRef(0);
  const [isAvatarRefreshing, setIsAvatarRefreshing] = useState(false);
  const logoTapCountRef = useRef(0);
  const logoTapTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showTrialCTA, setShowTrialCTA] = useState(false);
  const [hasAnimatedTrialCTA, setHasAnimatedTrialCTA] = useState(false);
  const [ctaDismissed, setCtaDismissed] = useState(false);
  const [isDesktopProfileOpen, setIsDesktopProfileOpen] = useState(false);
  const { session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { isAuthModalOpen, authContext, openAuthModal, closeAuthModal } = useAuthModal();
  const { data: appConfig } = useAppConfig();
  const { activeProfile, fetchProfileHierarchy } = useProfileHierarchy();
  const { registerNavigationPosition, isTransitionActive } = useSharedElement();
  const { isMiniPlayerVisible, isNowPlayingOpen, setNowPlayingOpen } = useRoxboxPlayerContext();
  
  // Expanded Dynamic Island state
  const [isIslandExpanded, setIsIslandExpanded] = useState(false);
  
  // Show glass header if scrolled OR if mini player is visible on mobile
  const showGlassHeader = isScrolled || (isMobile && isMiniPlayerVisible && location.pathname !== '/roxbox');
  
  // INSTANT PROFILE ACCESS: Read from snapshot first (sync, no DB call)
  // This is the key to eliminating the flash - we have profile data immediately
  const [instantProfile, setInstantProfile] = useState<ProfileSnapshot | null>(() => getProfileSnapshot());
  
  // Update instant profile when snapshot changes (e.g., after profile switch)
  useEffect(() => {
    const snapshot = getProfileSnapshot();
    if (snapshot) {
      setInstantProfile(snapshot);
      // Preload the avatar in background
      if (snapshot.avatar_url) {
        preloadAvatar(snapshot.avatar_url);
      }
    }
  }, [activeProfile?.id]); // Re-check when activeProfile changes
  
  // Listen for avatar changes and trigger refresh animation
  useEffect(() => {
    const unsubscribe = onAvatarChange(async (detail) => {
      console.log('[NAV] Avatar changed:', detail);
      
      // Preload new avatar
      if (detail.newUrl) {
        await preloadAvatar(detail.newUrl);
      }
      
      // Update instant profile with new avatar
      setInstantProfile(prev => prev ? { ...prev, avatar_url: detail.newUrl } : null);
      
      // Trigger spin animation after 1 second delay (when user navigates home)
      setTimeout(() => {
        setIsAvatarRefreshing(true);
        // End animation after spin completes
        setTimeout(() => {
          setIsAvatarRefreshing(false);
          // Refresh profile hierarchy to sync everything
          fetchProfileHierarchy();
        }, 600);
      }, 1000);
    });
    
    return unsubscribe;
  }, [fetchProfileHierarchy]);
  
  // Use instant profile data, fall back to hierarchy if no snapshot
  const avatarUrl = instantProfile?.avatar_url ?? activeProfile?.avatar_url ?? null;
  const displayName = instantProfile?.name ?? activeProfile?.name ?? session?.user?.email?.split('@')[0] ?? 'User';
  
  // Ref for the navigation profile avatar
  const navProfileRef = useRef<HTMLDivElement>(null);
  
  // Check if current profile is a kids profile
  // Check for kids profile - handle both snapshot format ('child') and DB format ('kids')
  const isKidsProfile = instantProfile?.profile_type === 'child' || activeProfile?.profile_type === 'kids';

  // Register navigation profile position for flying transition
  useEffect(() => {
    const updatePosition = () => {
      if (navProfileRef.current) {
        const rect = navProfileRef.current.getBoundingClientRect();
        // Calculate center position for perfect alignment
        // getBoundingClientRect already accounts for safe areas in the final screen position
        registerNavigationPosition({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
          width: rect.width,
          height: rect.height
        });
      }
    };
    
    // Update after layout settles - PWA safe areas may take a moment
    const rafId = requestAnimationFrame(() => {
      // First update
      updatePosition();
      // Second update after safe area CSS is applied
      setTimeout(updatePosition, 100);
      // Third update to catch any late layout shifts
      setTimeout(updatePosition, 300);
    });
    
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    
    // Use ResizeObserver for more accurate tracking
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updatePosition);
    });
    if (navProfileRef.current) {
      resizeObserver.observe(navProfileRef.current);
    }
    
    // Also observe the document for safe area changes (PWA mode)
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(updatePosition);
    });
    mutationObserver.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['style'] 
    });
    
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [registerNavigationPosition, isScrolled, avatarUrl, isMobile]);
  
  // Preload avatar in background if not ready
  useEffect(() => {
    if (avatarUrl && !isAvatarPreloaded(avatarUrl)) {
      preloadAvatar(avatarUrl);
    }
  }, [avatarUrl]);


  // Updated admin check to use the new user_accounts table
  const { data: isAdmin, isLoading: isAdminLoading } = useQuery({
    queryKey: ['isAdmin', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return false;
      console.log('Checking admin status for user:', session.user.id);

      // Check admin status from user_accounts table
      const { data: accountData, error: accountError } = await supabase
        .from('user_accounts')
        .select('is_admin')
        .eq('user_id', session.user.id)
        .maybeSingle();
        
      if (!accountError && accountData) {
        console.log('Admin status from user_accounts:', accountData.is_admin);
        return accountData.is_admin;
      }

      // Fallback to RPC function
      const { data: rpcData, error: rpcError } = await supabase.rpc('is_admin', {
        user_id: session.user.id
      });
      
      if (rpcError) {
        console.error('Error checking admin status:', rpcError);
        return false;
      }
      
      console.log('Admin status from RPC:', rpcData);
      return rpcData ?? false;
    },
    enabled: !!session?.user?.id
  });

  // Get subscription status
  const { data: subscriptionStatus } = useSubscriptionStatus();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 20);
      setIsScrollingDown(currentScrollY > lastScrollY.current && currentScrollY > 50);
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Trial CTA scroll visibility - lifted from MobileBottomNav to prevent state reset
  useEffect(() => {
    // Don't show if dismissed this session
    if (ctaDismissed) {
      setShowTrialCTA(false);
      return;
    }
    
    // Don't show trial CTA if user is not signed in
    if (!session) {
      setShowTrialCTA(false);
      return;
    }
    
    // Hide trial CTA if user has active subscription (period_end may be null for Apple subs)
    if (subscriptionStatus?.subscription_status === 'active') {
      if (!subscriptionStatus.current_period_end || new Date(subscriptionStatus.current_period_end) > new Date()) {
        setShowTrialCTA(false);
        return;
      }
    }
    
    // Also hide for cancelled subscriptions that still have time remaining
    if (subscriptionStatus?.subscription_status === 'cancelled' && subscriptionStatus?.current_period_end) {
      const periodEnd = new Date(subscriptionStatus.current_period_end);
      if (periodEnd > new Date()) {
        setShowTrialCTA(false);
        return;
      }
    }

    const handleTrialScroll = () => {
      if (ctaDismissed) return;
      const shouldShow = window.scrollY > 300;
      setShowTrialCTA(shouldShow);
      if (shouldShow && !hasAnimatedTrialCTA) {
        setHasAnimatedTrialCTA(true);
      }
    };

    // Check immediately on mount and when subscription status changes
    handleTrialScroll();

    window.addEventListener('scroll', handleTrialScroll);
    return () => window.removeEventListener('scroll', handleTrialScroll);
  }, [subscriptionStatus, hasAnimatedTrialCTA, ctaDismissed]);

  const handleLogout = async () => {
    try {
      await signOutCompletely(navigate);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive"
      });
    }
  };

  const { showProfiles } = useAppFlow();
  
  const handleSwitchProfiles = () => {
    // Clear the profile selection session storage to force the profile selection screen
    sessionStorage.removeItem('profile-selection-shown');
    showProfiles();
  };

  // Memoize user display data to prevent unnecessary re-renders
  const userDisplayData = useMemo(() => ({
    email: session?.user.email?.split("@")[0] || "User",
    fullEmail: session?.user.email ? maskEmail(session.user.email) : ""
  }), [session?.user.email]);

  // Desktop Profile Menu with beautiful block design - Auth aware
  const DesktopProfileMenu = useMemo(() => {
    if (isMobile) return null;

    return (
      <div className="flex items-center gap-3">
        <div
          className={`hidden md:block transition-all duration-300 overflow-hidden ${
            isScrollingDown ? 'max-w-0 opacity-0' : 'max-w-[200px] opacity-100'
          }`}
        >
          <SearchBar collapsed />
        </div>

        {session ? (
          <>
            <button
              onClick={() => setIsDesktopProfileOpen((v) => !v)}
              className="flex items-center"
            >
              <div
                ref={navProfileRef}
                data-nav-avatar="true"
                className={isAvatarRefreshing ? 'animate-spin' : ''}
                style={{
                  animationDuration: isAvatarRefreshing ? '0.6s' : undefined,
                  animationTimingFunction: 'ease-in-out',
                }}
              >
                <InstantAvatar
                  avatarUrl={avatarUrl}
                  fallbackText={displayName}
                  size="sm"
                  fallback="none"
                  className="h-9 w-9 border-2 border-white/20 hover:border-white/40 transition-colors duration-200 cursor-pointer"
                />
              </div>
            </button>

            {/* Desktop Profile Side Panel */}
            {isDesktopProfileOpen && (
              <>
                <div
                  className="fixed inset-0 z-[200]"
                  onClick={() => setIsDesktopProfileOpen(false)}
                />
                <div
                  className="fixed right-6 z-[201] p-[1.5px] animate-in fade-in slide-in-from-top-2 duration-200"
                  style={{
                    top: 'calc(env(safe-area-inset-top) + 90px)',
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.08) 100%)',
                    borderRadius: 24,
                  }}
                >
                  <div className="bg-black/90 backdrop-blur-xl rounded-[22.5px] p-5 w-80 shadow-[0_0_40px_rgba(0,0,0,0.6)]">
                    <div
                      className="grid gap-4"
                      style={{
                        fontFamily:
                          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
                      }}
                    >
                      <div className="flex items-center gap-4 pb-4 border-b border-white/10">
                        <StableAvatar
                          avatarUrl={avatarUrl}
                          fallbackText={displayName}
                          size="lg"
                          className="border-2 border-white/20"
                        />
                        <div>
                          <p className="text-lg font-medium text-white">{displayName}</p>
                          <p className="text-sm text-white/60">{userDisplayData.fullEmail}</p>
                          {!isAdminLoading && (
                            <div className="flex flex-col gap-0.5 mt-1">
                              {isAdmin && <p className="text-xs text-blue-400">Admin User</p>}
                              <p
                                className={`text-xs ${
                                  subscriptionStatus?.subscription_status === 'active'
                                    ? 'text-green-400'
                                    : subscriptionStatus?.subscription_status === 'cancelled'
                                      ? 'text-orange-400'
                                      : 'text-gray-400'
                                }`}
                              >
                                {subscriptionStatus?.subscription_status === 'active'
                                  ? '✓ Active Subscription'
                                  : subscriptionStatus?.subscription_status === 'cancelled'
                                    ? '⏰ Subscription Ending'
                                    : 'No Active Subscription'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid gap-3">
                        <button
                          onClick={() => {
                            handleSwitchProfiles();
                            setIsDesktopProfileOpen(false);
                          }}
                          className="flex items-center gap-4 p-4 bg-white/10 rounded-xl transition-all duration-200 hover:bg-white/15 text-left w-full border-0"
                        >
                          <div className="p-2 bg-white/10 rounded-lg">
                            <Users size={20} className="text-white" />
                          </div>
                          <div>
                            <span className="text-base font-medium text-white">Switch Profiles</span>
                            <p className="text-sm text-white/70">Change your reading profile</p>
                          </div>
                        </button>

                        {!isKidsProfile && (
                          <>
                            <button
                              onClick={() => {
                                navigate('/v2/profile');
                                window.scrollTo(0, 0);
                                setIsDesktopProfileOpen(false);
                              }}
                              className="flex items-center gap-4 p-4 bg-white/10 rounded-xl transition-all duration-200 hover:bg-white/15 text-left w-full border-0"
                            >
                              <div className="p-2 bg-white/10 rounded-lg">
                                <UserCircle size={20} className="text-white" />
                              </div>
                              <div>
                                <span className="text-base font-medium text-white">Profile</span>
                                <p className="text-sm text-white/70">Manage your account</p>
                              </div>
                            </button>

                            <button
                              onClick={() => {
                                navigate('/v2/profile');
                                window.scrollTo(0, 0);
                                setIsDesktopProfileOpen(false);
                              }}
                              className="flex items-center gap-4 p-4 bg-white/10 rounded-xl transition-all duration-200 hover:bg-white/15 text-left w-full border-0"
                            >
                              <div className="p-2 bg-white/10 rounded-lg">
                                <Settings size={20} className="text-white" />
                              </div>
                              <div>
                                <span className="text-base font-medium text-white">Settings</span>
                                <p className="text-sm text-white/70">Preferences and options</p>
                              </div>
                            </button>
                          </>
                        )}

                        {isAdmin && (
                          <button
                            onClick={() => {
                              navigate('/v2/profile');
                              window.scrollTo(0, 0);
                              setIsDesktopProfileOpen(false);
                            }}
                            className="flex items-center gap-4 p-4 bg-white/10 rounded-xl transition-all duration-200 hover:bg-white/15 text-left w-full border-0"
                          >
                            <div className="p-2 bg-white/10 rounded-lg">
                              <User size={20} className="text-white" />
                            </div>
                            <div>
                              <span className="text-base font-medium text-white">Admin</span>
                              <p className="text-sm text-white/70">Dashboard and controls</p>
                            </div>
                          </button>
                        )}
                      </div>

                      <div className="border-t border-white/10 pt-3">
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsDesktopProfileOpen(false);
                          }}
                          className="flex w-full items-center gap-4 p-4 bg-white/10 rounded-xl transition-all duration-200 hover:bg-white/15 text-left border-0"
                        >
                          <div className="p-2 bg-white/10 rounded-lg">
                            <LogOut size={20} className="text-white" />
                          </div>
                          <div>
                            <span className="text-base font-medium text-white">Log Out</span>
                            <p className="text-sm text-white/70">Sign out of your account</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <Button
            onClick={() => openAuthModal('Sign in to access your library and continue reading')}
            variant="ghost"
            className="text-white/70 hover:text-white hover:bg-transparent h-[34px] px-3 text-sm font-medium tracking-tight"
          >
            Sign In
          </Button>
        )}
      </div>
    );
  }, [
    isMobile,
    isScrollingDown,
    session,
    isDesktopProfileOpen,
    isAvatarRefreshing,
    avatarUrl,
    displayName,
    userDisplayData.fullEmail,
    isAdmin,
    isAdminLoading,
    subscriptionStatus?.subscription_status,
    isKidsProfile,
    handleLogout,
    handleSwitchProfiles,
    navigate,
    openAuthModal,
  ]);


  // Mobile profile sheet state
  const [isMobileProfileOpen, setIsMobileProfileOpen] = useState(false);

  // Mobile UserMenu - Auth aware - now uses bottom sheet drawer
  const MobileUserMenu = useMemo(() => {
    if (!session) {
      return (
        <button 
          onClick={() => openAuthModal("Sign in to access your library and continue reading")}
          className="text-white/90 text-sm font-medium tracking-tight px-3"
        >
          Sign In
        </button>
      );
    }

    return (
      <MobileProfileSheet
        isOpen={isMobileProfileOpen}
        onOpenChange={setIsMobileProfileOpen}
        avatarUrl={avatarUrl}
        displayName={displayName}
        email={userDisplayData.fullEmail}
        isAdmin={isAdmin ?? false}
        isKidsProfile={isKidsProfile}
        subscriptionStatus={subscriptionStatus}
        isAvatarRefreshing={isAvatarRefreshing}
        navProfileRef={navProfileRef}
        onSwitchProfiles={handleSwitchProfiles}
        onLogout={handleLogout}
        isCompact={(location.pathname === '/' || location.pathname === '/my-bundles') && isScrolled}
      />
    );
  }, [session, avatarUrl, displayName, userDisplayData, isAdmin, isKidsProfile, subscriptionStatus, handleLogout, handleSwitchProfiles, openAuthModal, isAvatarRefreshing, isMobileProfileOpen, isScrolled, location.pathname]);


  const isHomeRoute = location.pathname === '/';
  const isFloatingIslandRoute = isHomeRoute || location.pathname === '/my-bundles';

  const isReaderPage = location.pathname.startsWith('/read/');
  if (isReaderPage) return null;

  // Desktop Navigation Item Component - compact for floating island
  const DesktopNavItem = ({ href, children, isActive, onClick }: {
    href: string;
    children: React.ReactNode;
    isActive?: boolean;
    onClick?: (e: React.MouseEvent) => void;
  }) => (
    <button
      onClick={(e) => {
        if (onClick) {
          onClick(e);
          if (e.defaultPrevented) return;
        }
        navigate(href);
        window.scrollTo(0, 0);
      }}
      className={`px-3 py-1.5 rounded-full text-[13px] font-medium tracking-tight transition-all duration-300 ${
        isActive 
          ? 'text-white bg-white/15' 
          : 'text-white/60 hover:text-white hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );

  return (
    <>
      {/* Desktop/Tablet Navigation - Floating Island */}
      <nav 
        className={`${isMobile ? 'hidden' : 'fixed'} top-0 left-0 right-0 z-[110] flex justify-center transition-all duration-300`}
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top) + 20px)',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem'
        }}
      >
        {/* Glass container */}
        <div 
          className="flex items-center rounded-full transition-all duration-300 border-[0.5px] border-white/20"
          style={{
            background: 'rgba(40, 40, 44, 0.5)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            backdropFilter: 'blur(40px) saturate(180%)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.3), inset 0 0.5px 0 rgba(255,255,255,0.08)',
            padding: isScrollingDown ? '0.25rem 0.625rem' : '0.375rem 0.875rem',
            gap: isScrollingDown ? '0.625rem' : '0.875rem',
            minHeight: isScrollingDown ? '44px' : '50px',
          }}
        >
            {/* Logo - switches to mobile logo when scrolling down for compact mode */}
            <button 
              onClick={() => { navigate("/"); window.scrollTo(0, 0); }} 
              className="flex-shrink-0"
            >
              <img 
                alt="Block Bookster" 
                src={isScrollingDown 
                  ? (appConfig?.mobile_logo_url || "https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/more%20thick_.png")
                  : (appConfig?.desktop_logo_url || "https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos//sqaure%20wide.png")
                } 
                className={`object-contain transition-all duration-300 ${isScrollingDown ? 'h-9' : 'h-10'}`}
              />
            </button>
            
            {/* Nav Items - hide on scroll down, show on scroll up */}
            <div 
              className={`hidden md:flex items-center gap-1 transition-all duration-300 overflow-hidden ${
                isScrollingDown ? 'max-w-0 opacity-0' : 'max-w-[300px] opacity-100'
              }`}
            >
              <DesktopNavItem href="/" isActive={location.pathname === '/'}>
                Home
              </DesktopNavItem>
              <DesktopNavItem 
                href="/my-bundles" 
                isActive={location.pathname === '/my-bundles'}
                onClick={(e) => {
                  if (!session) {
                    e.preventDefault();
                    openAuthModal("Sign in to access your library");
                  }
                }}
              >
                My BUX
              </DesktopNavItem>
              <DesktopNavItem 
                href="/jukebox" 
                isActive={location.pathname === '/jukebox'}
              >
                Jukebox
              </DesktopNavItem>
            </div>
            
            {/* Desktop Mini Player - when playing */}
            {isMiniPlayerVisible && (location.pathname === '/' || location.pathname === '/my-bundles') && (
              <DesktopHeaderMiniPlayer />
            )}
            
            {/* Separator */}
            <div className="w-px h-6 bg-white/20 hidden lg:block" />

            {/* Right side: Search, Notifications, Profile */}
            {DesktopProfileMenu}
          </div>
      </nav>

      {/* Mobile Navigation */}
      {isMobile && (
        <>
      {/* Hide top header on jukebox */}
          {location.pathname !== '/jukebox' && (
            <div 
              className={`fixed left-0 right-0 z-[110] flex items-center justify-center transition-all duration-300 max-w-full overflow-visible ${
                isFloatingIslandRoute
                  ? '' 
                  : showGlassHeader ? 'glass-morphism border-b border-white/10' : 'bg-transparent'
              }`}
              style={{ 
                // Use safe area on Despia native, fixed 32px on web
                top: isDespiaRuntime() 
                  ? 'calc(var(--safe-top, 32px) + 8px)' 
                  : '32px',
                paddingLeft: '1rem',
                paddingRight: '1rem',
              }}
            >
              {/* Floating glass container - Dynamic Island effect on / and /my-bundles */}
              {/* Solid black container - no gradient border */}
              <div 
                className={`flex items-center ${
                  isFloatingIslandRoute
                    ? 'border' 
                    : ''
                }`}
                style={{
                  background: isFloatingIslandRoute
                    ? isHomeRoute
                      ? 'hsl(var(--card) / 0.5)'
                      : 'hsl(var(--background) / 0.96)'
                    : 'transparent',
                  WebkitBackdropFilter: isHomeRoute ? 'blur(28px) saturate(160%)' : 'none',
                  backdropFilter: isHomeRoute ? 'blur(28px) saturate(160%)' : 'none',
                  borderColor: isFloatingIslandRoute
                    ? isHomeRoute
                      ? 'hsl(var(--foreground) / 0.14)'
                      : 'hsl(var(--foreground) / 0.1)'
                    : 'transparent',
                  boxShadow: isFloatingIslandRoute
                    ? isHomeRoute
                      ? '0 10px 32px hsl(var(--background) / 0.42), inset 0 1px 0 hsl(var(--foreground) / 0.08)'
                      : '0 4px 24px hsl(var(--background) / 0.4)'
                    : 'none',
                  borderRadius: isFloatingIslandRoute ? 9999 : 0,
                  width: isFloatingIslandRoute ? 'auto' : '100%',
                  minHeight: isFloatingIslandRoute 
                    ? isMiniPlayerVisible 
                      ? isScrolled ? '36px' : '42px'
                      : isScrolled ? '32px' : '38px'
                    : '32px',
                  padding: isFloatingIslandRoute 
                    ? isScrolled ? '0.375rem 0.5rem' : '0.375rem 0.625rem'
                    : '0 0.5rem',
                  gap: '0.625rem',
                }}
              >
                  {/* Mini Player in Header - when playing */}
                  {isMiniPlayerVisible && (location.pathname === '/' || location.pathname === '/my-bundles') ? (
                    <DynamicIslandPlayer 
                      isExpanded={isIslandExpanded} 
                      setIsExpanded={setIsIslandExpanded}
                      isCompact={isScrolled} 
                    />
                  ) : session && (showGlassHeader || location.pathname === '/my-bundles') ? (
                    <button 
                      onClick={() => setIsMobileProfileOpen(true)}
                      className="flex items-center h-full py-2 flex-shrink-0 min-w-0"
                    >
                      <span 
                        className="text-white font-semibold truncate transition-all duration-300"
                        style={{
                          fontSize: isFloatingIslandRoute && isScrolled ? '0.8125rem' : '0.9375rem',
                        }}
                      >
                        For {getFirstName(displayName)}
                      </span>
                    </button>
                  ) : (
                    <button onClick={() => {
                      logoTapCountRef.current += 1;
                      if (logoTapTimerRef.current) clearTimeout(logoTapTimerRef.current);
                      if (logoTapCountRef.current >= 5) {
                        logoTapCountRef.current = 0;
                        navigate("/v2");
                        return;
                      }
                      logoTapTimerRef.current = setTimeout(() => {
                        if (logoTapCountRef.current < 5) {
                          navigate("/");
                          window.scrollTo(0, 0);
                        }
                        logoTapCountRef.current = 0;
                      }, 400);
                    }} className="flex items-center h-full py-2 flex-shrink-0 min-w-0">
                      <img 
                        alt="BUXBOX" 
                        src={appConfig?.mobile_logo_url || "https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/more%20thick_.png"} 
                        className={`w-auto object-contain object-left transition-all duration-300 ${
                          (location.pathname === '/') && isScrolled ? 'h-5' : 'h-7'
                        }`}
                      />
                    </button>
                  )}
                  {/* Avatar - always visible now since drawer handles expansion */}
                  <div 
                    className="flex-shrink-0"
                    style={{
                        marginLeft: isFloatingIslandRoute && isScrolled && !isMiniPlayerVisible ? '0' : 'auto',
                    }}
                  >
                    {MobileUserMenu}
                  </div>
                </div>
              </div>
            )}
          
          
          <MobileBottomNav 
            isNowPlayingOpen={isNowPlayingOpen}
            showTrialCTA={showTrialCTA && !ctaDismissed}
            setShowTrialCTA={setShowTrialCTA}
            hasAnimatedTrialCTA={hasAnimatedTrialCTA}
            isScrolled={isScrolled}
          />
        </>
      )}
      
      
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={closeAuthModal} 
        context={authContext}
        onAuthSuccess={() => {
          closeAuthModal();
          // Reload the page to refresh user state
          window.location.reload();
        }}
      />
    </>
  );
}
